import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShoppingBag, Gem, Loader2, ChevronRight, Trophy, Package, CheckCircle2,
  XCircle, Clock, Truck, X, Headphones, Coffee, Shirt, Gift, Fuel, Battery,
  Zap, TrendingUp, Palmtree, Info,
} from "lucide-react";
import { toast } from "sonner";
import { Gamification } from "../lib/api";
import { useConfirm } from "../components/ConfirmProvider";
import { PageSkeleton } from "../components/PageSkeleton";

const ICON_MAP = {
  headphones: Headphones, beach: Palmtree, zap: Zap, trending: TrendingUp,
  fuel: Fuel, gift: Gift, coffee: Coffee, shirt: Shirt, battery: Battery,
  package: Package,
};

const CATEGORY_META = {
  gadget:     { label: "Gadżety", tone: "from-purple-500 to-fuchsia-800" },
  perk:       { label: "Perki",   tone: "from-emerald-500 to-teal-800" },
  multiplier: { label: "Mnożniki",tone: "from-amber-500 to-orange-700" },
  voucher:    { label: "Vouchery",tone: "from-sky-500 to-blue-800" },
};

const STATUS_META = {
  pending:   { label: "Oczekuje",     tone: "bg-amber-100 text-amber-800 ring-amber-200",       icon: Clock },
  approved:  { label: "Zaakceptowane",tone: "bg-sky-100 text-sky-800 ring-sky-200",             icon: CheckCircle2 },
  delivered: { label: "Wydane",       tone: "bg-emerald-100 text-emerald-800 ring-emerald-200", icon: Truck },
  rejected:  { label: "Odrzucone",    tone: "bg-red-100 text-red-800 ring-red-200",             icon: XCircle },
};

export default function ShopPage() {
  const [data, setData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("shop"); // shop | orders
  const { confirm } = useConfirm();

  const load = () => Promise.all([Gamification.shop(), Gamification.myOrders()])
    .then(([s, o]) => { setData(s); setOrders(o.orders || []); })
    .catch(() => toast.error("Nie mogę pobrać sklepu"))
    .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    if (!data?.items) return {};
    return data.items.reduce((acc, it) => {
      const k = it.category || "gadget";
      (acc[k] ||= []).push(it);
      return acc;
    }, {});
  }, [data]);

  const buy = async (item) => {
    if ((data?.diamonds ?? 0) < item.price_diamonds) {
      toast.error(`Za mało diamentów. Masz ${data?.diamonds}💎, potrzeba ${item.price_diamonds}💎.`);
      return;
    }
    const ok = await confirm(
      `Zamówić „${item.name}"? Zostanie potrącone ${item.price_diamonds}💎. Zamówienie trafi do akceptacji admina.`,
      { confirmLabel: `Zapłać ${item.price_diamonds}💎`, tone: "info" },
    );
    if (!ok) return;
    try {
      await Gamification.placeOrder(item.id);
      toast.success(`Zamówienie „${item.name}" zostało złożone.`);
      load();
      setTab("orders");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nie mogę złożyć zamówienia");
    }
  };

  if (loading) {
    return (
      <PageSkeleton label="Ładuję sklep…" />
    );
  }

  return (
    <div className="p-3 md:p-6 space-y-5" data-testid="shop-page">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-cyan-500 via-blue-700 to-zinc-950 text-white p-6 md:p-8 ring-1 ring-white/10 shadow-[0_25px_60px_-25px_rgba(0,0,0,0.7)]">
        <Gem className="absolute -right-10 -bottom-16 w-72 h-72 opacity-[0.10]" strokeWidth={1.5} />
        <div className="relative flex items-center gap-2">
          <div className="w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center ring-1 ring-white/25">
            <ShoppingBag className="w-5 h-5" />
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-white/70">
            // Sklep GoPossible
          </div>
        </div>
        <h1 className="relative mt-3 font-heading font-black text-3xl md:text-5xl uppercase tracking-tight">
          {(data?.diamonds ?? 0).toLocaleString("pl-PL")} <Gem className="inline w-8 h-8 -mt-2" />
        </h1>
        <div className="relative mt-1 text-white/80">
          Wymień diamenty na nagrody, bony i mnożniki. Zbierasz je za odblokowywanie osiągnięć.
        </div>

        <div className="relative mt-5 flex items-center gap-2">
          <Link
            to="/osiagniecia"
            data-testid="btn-goto-achievements"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white text-blue-900 font-bold uppercase text-xs tracking-widest hover:bg-cyan-100 transition-colors"
          >
            <Trophy className="w-4 h-4" /> Osiągnięcia
            <ChevronRight className="w-4 h-4" />
          </Link>
          <div className="ml-auto flex gap-2">
            <TabBtn active={tab === "shop"} onClick={() => setTab("shop")} testId="tab-shop">Sklep</TabBtn>
            <TabBtn active={tab === "orders"} onClick={() => setTab("orders")} testId="tab-orders">
              Moje zamówienia ({orders.length})
            </TabBtn>
          </div>
        </div>
      </div>

      {tab === "shop" && (
        <div className="space-y-6">
          {Object.entries(grouped).map(([cat, list]) => {
            const meta = CATEGORY_META[cat] || { label: cat, tone: "from-zinc-500 to-zinc-800" };
            return (
              <section key={cat} data-testid={`shop-category-${cat}`}>
                <div className="flex items-center gap-2 mb-3">
                  <div className={`h-2 w-6 rounded-full bg-gradient-to-r ${meta.tone}`} />
                  <h2 className="font-heading font-black uppercase tracking-tight text-lg text-zinc-900">
                    {meta.label}
                  </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {list.map((it) => (
                    <ShopCard
                      key={it.id}
                      item={it}
                      balance={data?.diamonds ?? 0}
                      onBuy={() => buy(it)}
                      tone={meta.tone}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {tab === "orders" && (
        <OrdersSection orders={orders} />
      )}
    </div>
  );
}

function ShopCard({ item, balance, onBuy, tone }) {
  const Icon = ICON_MAP[item.icon] || Package;
  const affordable = balance >= item.price_diamonds;
  const soldOut = item.stock !== null && item.stock !== undefined && item.stock <= 0;
  return (
    <div
      data-testid={`shop-item-${item.code}`}
      className={`relative overflow-hidden rounded-2xl bg-white ring-1 ring-zinc-200 p-4 min-h-[220px] flex flex-col hover:ring-zinc-400 transition-all shadow-sm hover:shadow-lg`}
    >
      <div className={`absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r ${tone}`} />
      <Icon
        aria-hidden
        className="absolute -right-4 -bottom-4 w-28 h-28 opacity-[0.06]"
        strokeWidth={1.5}
      />
      <div className="relative flex items-center gap-2">
        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${tone} text-white flex items-center justify-center ring-1 ring-white/25`}>
          <Icon className="w-5 h-5" />
        </div>
        {soldOut && (
          <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-mono uppercase tracking-widest">
            Wyprzedane
          </div>
        )}
        {item.stock !== null && item.stock !== undefined && !soldOut && (
          <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-mono uppercase tracking-widest">
            {item.stock} szt.
          </div>
        )}
      </div>

      <div className="relative mt-3 flex-1">
        <div className="font-heading font-black text-lg uppercase tracking-tight text-zinc-900">
          {item.name}
        </div>
        <div className="text-xs mt-1 text-zinc-600">
          {item.description}
        </div>
      </div>

      <div className="relative mt-4 flex items-center justify-between">
        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-cyan-50 text-cyan-800 ring-1 ring-cyan-200 font-heading font-black">
          <Gem className="w-4 h-4" /> {item.price_diamonds.toLocaleString("pl-PL")}
        </div>
        <button
          onClick={onBuy}
          disabled={!affordable || soldOut}
          data-testid={`btn-buy-${item.code}`}
          className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-widest transition-colors ${
            affordable && !soldOut
              ? "bg-zinc-900 text-white hover:bg-brand"
              : "bg-zinc-100 text-zinc-400 cursor-not-allowed"
          }`}
        >
          {soldOut ? "Brak" : affordable ? "Kup" : "Za mało"}
        </button>
      </div>
    </div>
  );
}

function OrdersSection({ orders }) {
  if (!orders.length) {
    return (
      <div className="p-12 text-center text-zinc-500 text-sm rounded-2xl bg-white ring-1 ring-zinc-200">
        Nie masz jeszcze żadnych zamówień. Idź do sklepu, znajdź coś fajnego!
      </div>
    );
  }
  return (
    <div className="rounded-2xl bg-white ring-1 ring-zinc-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-zinc-100 bg-zinc-50 flex items-center gap-2">
        <Package className="w-4 h-4 text-zinc-500" />
        <div className="text-sm font-bold text-zinc-800">Moje zamówienia</div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 ml-auto">
          {orders.length}
        </div>
      </div>
      <div>
        {orders.map((o) => {
          const st = STATUS_META[o.status] || STATUS_META.pending;
          const SIcon = st.icon;
          return (
            <div key={o.id} data-testid={`order-row-${o.id}`}
                 className="px-4 py-3 border-b border-zinc-100 last:border-0 flex items-center gap-4">
              <div className="w-8 h-8 rounded-lg bg-zinc-100 flex items-center justify-center">
                <Package className="w-4 h-4 text-zinc-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-zinc-900 truncate">{o.item_name}</div>
                <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
                  {new Date(o.requested_at).toLocaleString("pl-PL")}
                  {o.admin_note && <span className="ml-2 italic">· „{o.admin_note}&rdquo;</span>}
                </div>
              </div>
              <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-800 text-xs font-heading font-black">
                <Gem className="w-3 h-3" /> {o.price_paid}
              </div>
              <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ring-1 text-[10px] font-mono uppercase tracking-widest ${st.tone}`}>
                <SIcon className="w-3 h-3" /> {st.label}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, testId, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-2 rounded-xl text-xs font-bold uppercase tracking-widest transition-colors ${
        active ? "bg-white text-blue-900" : "bg-white/10 text-white/80 hover:bg-white/20"
      }`}
    >
      {children}
    </button>
  );
}
